import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';

export interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  /** Verb-first label: "Create task", not "Submit". */
  submitLabel: string;
  /** Primary button stays disabled until the minimum is filled. */
  canSubmit?: boolean;
  isSubmitting?: boolean;
  /** When true, closing needs a double-Esc and backdrop clicks don't discard. */
  dirty?: boolean;
  /** Context chip rendered top-left (project, parent entity, …). */
  context?: React.ReactNode;
  /** Left side of the footer ("+ more fields", "create more", …). */
  footerLeft?: React.ReactNode;
  width?: 'md' | 'lg';
  stableHeight?: boolean;
  'aria-label': string;
  children: React.ReactNode;
}

const FOCUSABLE =
  'input, textarea, select, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);
const submitHint = isMac ? '⌘⏎' : 'Ctrl⏎';

/**
 * Create-dialog shell: centered modal over the current page, so context stays
 * visible. Children mount only while open (auto-grow fields measure correctly).
 * Esc closes (double-press to discard when dirty), ⌘⏎ submits from anywhere,
 * focus is trapped inside and returned to the trigger on close.
 * See docs/ux/14-forms.md.
 */
export function FormDialog({
  open,
  onClose,
  onSubmit,
  submitLabel,
  canSubmit = true,
  isSubmitting = false,
  dirty = false,
  context,
  footerLeft,
  width = 'md',
  stableHeight = false,
  'aria-label': ariaLabel,
  children,
}: FormDialogProps) {
  const { t } = useI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const [escArmed, setEscArmed] = useState(false);

  const requestClose = useCallback(() => {
    if (!dirty) {
      onClose();
      return;
    }
    if (escArmed) {
      onClose();
    } else {
      setEscArmed(true);
    }
  }, [dirty, escArmed, onClose]);

  useEffect(() => {
    if (!open) return;
    setEscArmed(false);
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    const target =
      panel?.querySelector<HTMLElement>('[data-autofocus]') ||
      panel?.querySelector<HTMLElement>('input, textarea');
    target?.focus();

    return () => previouslyFocused?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open || !escArmed) return;
    const timer = setTimeout(() => setEscArmed(false), 3000);
    return () => clearTimeout(timer);
  }, [open, escArmed]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (canSubmit && !isSubmitting) onSubmit();
        return;
      }
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (!focusables?.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      setEscArmed(false);
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, requestClose, canSubmit, isSubmitting, onSubmit]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={requestClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={cn(
          'absolute left-1/2 top-[16vh] -translate-x-1/2',
          'flex w-full flex-col',
          stableHeight ? 'h-[70vh] max-h-[84vh]' : 'max-h-[70vh]',
          width === 'md' ? 'max-w-[560px]' : 'max-w-[680px]',
          'rounded-2xl border border-border bg-popover shadow-2xl'
        )}
      >
        <div className="flex items-center gap-2 px-3.5 pt-3">
          {context}
          <button
            type="button"
            onClick={requestClose}
            aria-label={t('formKit.close')}
            className="ml-auto grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        <div className="flex items-center gap-3 border-t border-border px-3.5 py-2.5">
          {footerLeft}
          {escArmed && (
            <span className="text-xs text-muted-foreground" role="status">
              {t('formKit.escToDiscard')}
            </span>
          )}
          <span className="flex-1" />
          <Button type="button" onClick={onSubmit} disabled={!canSubmit} isLoading={isSubmitting}>
            {submitLabel}
            <span className="ml-2 border-l border-primary-foreground/30 pl-2 font-mono text-[10.5px] opacity-75">
              {submitHint}
            </span>
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
